/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Shared template dependencies for the eagerly loaded advertising features.
 */
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {TimepickerModule} from 'ngx-bootstrap/timepicker';

import {APP_SHARED} from '../common/app-shared';
import {ADMIN_RESOURCE_PAGES} from '../common/controller/admin-resource-pages';
import {PercentageBarComponent} from '../common/controller/progress-bar-percentage-component';
import {VelocityComponent} from '../common/controller/velocity-component';
import {RateLimitComponent} from '../common/controller/rate-limit.component';
import {ListColumnSelectorComponent} from '../common/controller/list-column-selector.component';
import {QueryColumnSelectorComponent} from '../common/controller/query-column-selector.component';
import {CdnStatsComponent} from '../common/controller/cdn-stats.component';
import {Co2StatsComponent} from '../common/controller/co2-stats.component';
import {FloorPriceComponent} from '../common/controller/floor-price.component';
import {InactiveTargetsComponent} from '../common/controller/inactive-targets.component';
import {PublisherInfoComponent} from '../common/controller/publisher-info.component';
import {ScannedCreativesComponent} from '../common/controller/scanned-creatives.component';
import {SearchSelectComponent} from '../common/controller/search-select.component';
import {AdnTeamSelectorComponent} from '../common/controller/team-selector.component';
import {ColumnSelectorComponent} from '../common/controller/column-selector.component';
import {TopArticlesScreenshottingModalComponent} from '../common/controller/top-articles-screenshotting-modal.component';
import {TopArticlesTableComponent} from '../common/controller/top-articles-table.component';
import {TopDomainTableComponent} from '../common/controller/top-domain-table.component';
import {TopNSelectorComponent} from '../common/controller/top-n-selector.component';
import {TopStatsTableComponent} from '../common/controller/top-stats-table.component';
import {TopTargetingTableComponent} from '../common/controller/top-targeting-table.component';
import {TopStatsTableHostComponent} from '../common/directives/top-stats-table-host.component';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {ADMIN_LIST} from '../common/directives/admin-list';
import {CHARTS} from '../common/directives/charts';
import {CodemirrorFieldComponent} from '../common/directives/codemirror-field.component';
import {AdnCountrySelectComponent} from '../common/directives/country-select/adn-country-select.component';
import {CountrySelectComponent} from '../common/directives/country-select/country-select';
import {ExplorationComponent} from '../common/directives/exploration.component';
import {ExplorationTableComponent} from '../common/directives/exploration-table.component';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {AdminRichTextFieldComponent} from '../common/directives/notes/admin-rich-text-field.component';
import {NotesComponent} from '../common/directives/notes/notes.component';
import {ObjectNotesButtonComponent} from '../common/directives/notes/object-notes-button.component';
import {ObjectStatesComponent} from '../common/directives/object-states.component';
import {ViewObjectStatesComponent} from '../common/directives/view-object-states.component';
import {ConfirmObjectStateModalComponent} from '../common/directives/confirm-object-state-modal.component';
import {ListSelectInFilterComponent} from '../common/directives/list-select-in-filter.component';
import {ExternalAdUnitsComponent} from '../common/directives/external-ad-units.component';
import {AdStatusLabelComponent} from '../ad-unit/ad-status-component';
import {AdvertiserStatusLabelComponent} from '../advertiser/advertiser-status-component';
import {CREATIVE_ASSETS} from '../creative/creative-assets';
import {CreativeDimsOutputComponent} from '../common/controller/creative-dims-output-component';
import {ContainFullScreenPreviewDirective} from '../creative/ad/contain-full-screen-preview.directive';
import {CreativeDirectComponent} from '../creative/ad/creative-direct.component';
import {CreativePreviewComponent} from '../creative/ad/creative-preview.component';
import {CreativePreviewViaIdComponent} from '../creative/ad/creative-preview-via-id.component';
import {CreativePreviewViaLayoutComponent} from '../creative/ad/creative-preview-via-layout.component';
import {CreativeRenderedComponent} from '../creative/ad/creative-rendered.component';
import {CreativeAdvertiserMismatchFixComponent} from '../creative/creative-advertiser-mismatch-fix.component';
import {CreativePreviewModalComponent} from '../creative/creative-preview-modal.component';
import {CreativeTagComponent} from '../creative/creative-tag.component';
import {LINE_ITEM_LABELS} from '../line-item/line-item-labels';
import {BudgetViewComponent} from '../order/budget-view.component';
import {PaymentStatusLabelComponent} from '../payment/payment-status-component';
import {AuditHistoryComponent} from '../report/widget/audit-history.component';
import {ReportRequesterComponent} from '../report/widget/report-requester.component';
import {ReportSchedulesComponent} from '../report/widget/report-schedules.component';
import {TARGETING_COMPONENTS} from '../targeting/targeting-components';

export const ADVERTISING_SHARED = [
  BudgetViewComponent,
  ...APP_SHARED,
  FormsModule,
  TranslateModule,
  UIRouterModule,
  ButtonsModule,
  CollapseModule,
  BsDatepickerModule,
  BsDropdownModule,
  PopoverModule,
  TabsModule,
  TimepickerModule,
  ...ADMIN_LIST,
  ...ADMIN_RESOURCE_PAGES,
  AdnSelectComponent,
  ...CHARTS,
  CodemirrorFieldComponent,
  AdnCountrySelectComponent,
  CountrySelectComponent,
  ...CREATIVE_ASSETS,
  ContainFullScreenPreviewDirective,
  CreativeAdvertiserMismatchFixComponent,
  CreativeDimsOutputComponent,
  CreativeDirectComponent,
  CreativePreviewComponent,
  CreativePreviewModalComponent,
  CreativePreviewViaIdComponent,
  CreativePreviewViaLayoutComponent,
  CreativeRenderedComponent,
  CreativeTagComponent,
  PercentageBarComponent,
  VelocityComponent,
  RateLimitComponent,
  ExplorationComponent,
  ExplorationTableComponent,
  ...FORM_CONTROLS,
  ...LINE_ITEM_LABELS,
  ListColumnSelectorComponent,
  QueryColumnSelectorComponent,
  ListSelectInFilterComponent,
  AdminRichTextFieldComponent,
  NotesComponent,
  ObjectNotesButtonComponent,
  ObjectStatesComponent,
  ViewObjectStatesComponent,
  ConfirmObjectStateModalComponent,
  PaymentStatusLabelComponent,
  CdnStatsComponent,
  Co2StatsComponent,
  ExternalAdUnitsComponent,
  FloorPriceComponent,
  InactiveTargetsComponent,
  PublisherInfoComponent,
  ScannedCreativesComponent,
  AuditHistoryComponent,
  ReportRequesterComponent,
  ReportSchedulesComponent,
  AdStatusLabelComponent,
  AdvertiserStatusLabelComponent,
  SearchSelectComponent,
  AdnTeamSelectorComponent,
  ColumnSelectorComponent,
  TopArticlesScreenshottingModalComponent,
  TopArticlesTableComponent,
  TopDomainTableComponent,
  TopNSelectorComponent,
  TopStatsTableComponent,
  TopStatsTableHostComponent,
  TopTargetingTableComponent,
  ...TARGETING_COMPONENTS
];
